import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Target, BookOpen, Camera, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export default function WelcomeModal() {
  const { user, profile, refreshProfile } = useAuth();
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);

  if (!open || profile?.has_seen_welcome) return null;

  const dismiss = async () => {
    setOpen(false);
    if (user) {
      await supabase
        .from("profiles")
        .update({ has_seen_welcome: true })
        .eq("user_id", user.id);
      refreshProfile();
    }
  };

  const slides = [
    {
      icon: Heart,
      title: "Welcome to Together OS",
      body: "We built this for couples who want to be intentional about their relationship — not just busy. This is your private space to dream, plan, and reflect together.",
    },
    {
      icon: Target,
      title: "Set goals that matter",
      body: "Create shared goals across finances, health, family, and personal growth. AI helps you define clear key results, and you track progress at your own pace.",
    },
    {
      icon: BookOpen,
      title: "Reflect monthly",
      body: "Once a month, take time for an honest board meeting. What went well? What didn't? These reflections become the heartbeat of your shared journey.",
    },
    {
      icon: Camera,
      title: "Capture what matters",
      body: "Upload photos and memories to your shared gallery. Every time you open the app, you'll be reminded of the moments that brought you closer.",
    },
  ];

  const current = slides[step];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-foreground/40 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && dismiss()}
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          className="bg-card border border-border rounded-xl w-full max-w-md overflow-hidden"
        >
          <div className="p-8 text-center space-y-5">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <current.icon
                className="w-10 h-10 text-accent mx-auto"
                fill={current.icon === Heart ? "currentColor" : "none"}
              />
              <h2 className="font-display text-xl font-semibold text-foreground">
                {current.title}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {current.body}
              </p>
            </motion.div>

            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 pt-2">
              {slides.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === step ? "bg-primary" : "bg-secondary"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="px-8 pb-8 flex justify-between items-center">
            <button
              onClick={dismiss}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip
            </button>
            {step < slides.length - 1 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={dismiss}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Let's begin <Heart className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
